import type React from 'react';
import { useState, useEffect, useCallback } from 'react';

import { dataSchema } from '@ragu-components/ord-types';
import { useReactFlow } from '@xyflow/react';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import SortableInstructionsList from '../../components/SortableInstructionsList';
import NodeControlForm from '../NodeControlForm';

import type { ExtensionInstruction, CallflowNodeInstruction, NodeControlsProps } from '../../dataProvider/types';

export interface ExtensionType extends Record<string, unknown> {
  instructions: ExtensionInstruction[];
}

interface ExtensionControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: ExtensionType;
}

export default function ExtensionControls({ updateNodeData, data }: ExtensionControlsProps): React.ReactElement {
  const [instructions, setInstructions] = useState<ExtensionInstruction[]>(data.instructions ?? []);
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();

  const removeEdgesByHandles = useCallback(
    (handles: string[]) => {
      if (handles.length === 0 || reactFlowInstance === undefined) {
        return;
      }
      const edges = reactFlowInstance.getEdges();
      const filteredEdges = edges.filter((edge) => {
        if (edge.sourceHandle === undefined || edge.sourceHandle === null) {
          return true;
        }
        return !handles.includes(edge.sourceHandle);
      });
      if (filteredEdges.length !== edges.length) {
        flowchartLogger.debug('ExtensionControls: deleting edges for handles', { handles });
        reactFlowInstance.setEdges(filteredEdges);
      }
    },
    [reactFlowInstance]
  );

  const onExtensionDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    updateNodeData({ instructions });
  };

  useEffect(() => {
    flowchartLogger.debug('ExtensionControls: autosave', { instructions });
    updateNodeData({ instructions });
  }, [updateNodeData, instructions]);

  const updateIfValid = useCallback((): void => {
    if (
      // Have to check for null to avoid TS errors
      instructions === null ||
      instructions.length === 0 ||
      instructions.find(
        (instruction) => instruction.match_exit_causes === null || instruction.match_exit_causes.length === 0
      ) !== undefined
    ) {
      flowchartLogger.debug('ExtensionControls: invalid form data, update cancelled');
      return;
    }
    flowchartLogger.debug('ExtensionControls: node data updated');
    updateNodeData({ instructions });
  }, [instructions, updateNodeData]);

  const handleInstructionDelete = useCallback(
    (handleId: string) => {
      flowchartLogger.debug('ExtensionControls: removing instruction handle', { handleId });
      removeEdgesByHandles([handleId]);
      updateIfValid();
    },
    [removeEdgesByHandles, updateIfValid]
  );

  return (
    <NodeControlForm onSubmit={onExtensionDataUpdate} data={data} updateNodeData={updateNodeData}>
      <SortableInstructionsList
        inputs={instructions}
        onChange={setInstructions as React.Dispatch<React.SetStateAction<CallflowNodeInstruction[]>>}
        onDelete={handleInstructionDelete}
        matchExitCauses={dataSchema.CallflowExtension.properties.connections.items.properties.match_exit_causes.items.enum.map(
          (choice) => choice
        )}
        presets={[
          {
            description: translate('callflows.components.sortableInstructionsList.presets.busy'),
            match_exit_causes: ['USER_BUSY', 'BUSY_EVERYWHERE', 'CALL_REJECTED', 'DECLINE'],
          },
        ]}
      />
    </NodeControlForm>
  );
}
